import type { GeoFix } from '../contracts';
import { DEG2RAD, RAD2DEG, wrap360 } from './math';

export interface SpeedOptions {
  /** Doppler speeds within this window (ms, relative to the newest one) are averaged. */
  windowMs: number;
  /** Doppler data older than this (ms) falls back to the Kalman velocity. */
  dopplerMaxAgeMs: number;
  /** No fix for this long (ms) -> speed unknown. */
  maxFixAgeMs: number;
  /** Course is reported only above this speed (km/h). */
  minCourseKmh: number;
}

export const DEFAULT_SPEED_OPTIONS: SpeedOptions = {
  windowMs: 2000,
  dopplerMaxAgeMs: 3000,
  maxFixAgeMs: 10000,
  minCourseKmh: 1,
};

const RING = 8;

/**
 * Speed over ground / course: GPS Doppler speed (fix.speed) averaged over ~2 s
 * when available, else the Kalman velocity. Course only when moving > 1 km/h.
 */
export class SpeedEstimator {
  readonly opts: SpeedOptions;
  speedKmh: number | null = null;
  courseDeg: number | null = null;
  /** 'doppler' | 'kalman' | null: source of the last speed value. */
  source: 'doppler' | 'kalman' | null = null;

  private readonly times = new Float64Array(RING);
  private readonly speeds = new Float64Array(RING);
  /** Course (deg) or NaN. */
  private readonly courses = new Float64Array(RING);
  private count = 0;
  private head = 0;
  private lastFixTime = -Infinity;
  private newestHasSpeed = false;

  constructor(opts: Partial<SpeedOptions> = {}) {
    this.opts = { ...DEFAULT_SPEED_OPTIONS, ...opts };
  }

  reset(): void {
    this.count = 0;
    this.head = 0;
    this.lastFixTime = -Infinity;
    this.newestHasSpeed = false;
    this.speedKmh = null;
    this.courseDeg = null;
    this.source = null;
  }

  addFix(fix: GeoFix): void {
    if (fix.time < this.lastFixTime) return;
    this.lastFixTime = fix.time;
    const sp = fix.speed;
    this.newestHasSpeed = sp !== null && Number.isFinite(sp) && sp >= 0;
    if (!this.newestHasSpeed) return;
    this.times[this.head] = fix.time;
    this.speeds[this.head] = sp as number;
    this.courses[this.head] = fix.course !== null && Number.isFinite(fix.course) ? fix.course : NaN;
    this.head = (this.head + 1) % RING;
    if (this.count < RING) this.count++;
  }

  /**
   * @param kalmanValid whether (ve, vn) is usable
   * @param ve,vn Kalman velocity (m/s) in frame axes
   * @param corrDeg LocalFrame.headingCorrectionDeg at the user (frame heading = true + corr)
   */
  update(now: number, kalmanValid: boolean, ve: number, vn: number, corrDeg: number): void {
    const o = this.opts;
    if (now - this.lastFixTime > o.maxFixAgeMs) {
      this.speedKmh = null;
      this.courseDeg = null;
      this.source = null;
      return;
    }
    const newest = (this.head + RING - 1) % RING;
    const newestTime = this.count > 0 ? this.times[newest] : -Infinity;
    const useDoppler = this.newestHasSpeed && now - newestTime <= o.dopplerMaxAgeMs;

    let speedMs = NaN;
    let courseE = 0;
    let courseN = 0;
    let haveCourse = false;
    if (useDoppler) {
      let sum = 0;
      let n = 0;
      for (let k = 0; k < this.count; k++) {
        const i = (newest - k + RING) % RING;
        if (newestTime - this.times[i] > o.windowMs + 100) break;
        sum += this.speeds[i];
        n++;
        const c = this.courses[i];
        if (!Number.isNaN(c)) {
          // speed-weighted circular mean
          courseE += this.speeds[i] * Math.sin(c * DEG2RAD);
          courseN += this.speeds[i] * Math.cos(c * DEG2RAD);
          haveCourse = true;
        }
      }
      speedMs = sum / n;
      this.source = 'doppler';
    } else if (kalmanValid) {
      speedMs = Math.sqrt(ve * ve + vn * vn);
      this.source = 'kalman';
    } else {
      this.source = null;
    }

    if (Number.isNaN(speedMs)) {
      this.speedKmh = null;
      this.courseDeg = null;
      return;
    }
    this.speedKmh = speedMs * 3.6;
    if (this.speedKmh <= o.minCourseKmh) {
      this.courseDeg = null;
    } else if (haveCourse && (courseE !== 0 || courseN !== 0)) {
      this.courseDeg = wrap360(Math.atan2(courseE, courseN) * RAD2DEG);
    } else if (kalmanValid && (ve !== 0 || vn !== 0)) {
      this.courseDeg = wrap360(Math.atan2(ve, vn) * RAD2DEG - corrDeg);
    } else {
      this.courseDeg = null;
    }
  }
}
